"use client";

import Image from "next/image";

import { BV_INLINE_RATING_PRODUCT_TYPES } from "@/components/bazaarvoice/bazaarvoice.constants";
import { BvWidgetContainer } from "@/components/bazaarvoice/bvWidget.container";
import { SalesPrice } from "@/components/salesPrice/salesPrice.component";
import { Text } from "@/components/text/text.component";

import {
  buildEcommerceEvent,
  createEcommercePayload,
  mapProductToItem,
  mapProductTypeToAnalyticsCategory,
} from "@/lib/analytics/ecommerce-analytics";
import { setItemListOrigin } from "@/lib/analytics/item-list-context";

import { EVENTS } from "@/constants/analytics.constants";
import { useSettings } from "@/contexts/settingsProvider.context";
import { useTagging } from "@/hooks/useTagging";

import {
  BUILD_PROTECTION_ITEM_LIST_ID,
  BUILD_PROTECTION_ITEM_LIST_NAME,
} from "./buildProtection.constants";
import type { BuildProtectionProductItem } from "./buildProtection.types";
import { BuildProtectionCta } from "./buildProtectionCta.component";

export function BuildProtectionProductCard({
  item,
  index,
  viewDetailsLabel,
}: BuildProtectionProductCardProps) {
  const product = item.product;
  const settings = useSettings();
  const [, setTag] = useTagging();

  if (!product?.name) return null;

  const chips = product.chips?.filter(Boolean) ?? [];
  const showRating = Boolean(
    settings?.enableBazaarvoice &&
    product.id &&
    product.type &&
    BV_INLINE_RATING_PRODUCT_TYPES.includes(product.type),
  );

  const handleViewDetails = () => {
    setItemListOrigin({
      item_list_id: BUILD_PROTECTION_ITEM_LIST_ID,
      item_list_name: BUILD_PROTECTION_ITEM_LIST_NAME,
      item_id: product.id ?? undefined,
      item_slug: product.slug ?? undefined,
    });

    const analyticsItem = mapProductToItem(
      {
        id: product.id ?? undefined,
        slug: product.slug ?? undefined,
        name: product.name ?? undefined,
        listPrice: product.listPrice,
        salesPrice: product.salesPrice,
        type: product.type,
      },
      {
        index,
        category: mapProductTypeToAnalyticsCategory(product.type, product.id),
      },
    );

    setTag(
      buildEcommerceEvent(
        EVENTS.SELECT_ITEM,
        createEcommercePayload([analyticsItem], {
          item_list_id: BUILD_PROTECTION_ITEM_LIST_ID,
          item_list_name: BUILD_PROTECTION_ITEM_LIST_NAME,
        }),
      ),
      { reset: true },
    );
  };

  return (
    <article
      data-testid="build-protection-product-card"
      className="border-greyscale-background flex h-full flex-col gap-4 rounded-2xl border bg-white p-6 shadow-sm">
      {chips.length > 0 ? (
        <div className="flex flex-wrap gap-2">
          {chips.map((chip) => (
            <Text
              key={chip}
              variant="body4"
              component="p"
              className="bg-primary-100 text-secondary w-fit rounded-full px-2 py-0.5 text-xs font-semibold">
              {chip}
            </Text>
          ))}
        </div>
      ) : (
        <span className="h-6" aria-hidden />
      )}
      <div className="flex h-40 items-center justify-center">
        {product.featuredImage?.url ? (
          <Image
            src={product.featuredImage.url}
            alt={product.featuredImage.alt || product.name}
            width={280}
            height={160}
            className="max-h-36 w-auto object-contain"
          />
        ) : null}
      </div>
      <Text variant="h6" component="h3" className="text-greyscale-headline">
        {product.name}
      </Text>
      {showRating ? (
        <BvWidgetContainer
          show="inline_rating"
          productId={product.id ?? ""}
          className="empty:hidden"
        />
      ) : null}
      {product.shortDescription ? (
        <Text
          variant="body1"
          component="p"
          className="text-greyscale-body line-clamp-3">
          {product.shortDescription}
        </Text>
      ) : null}
      <div className="mt-auto flex flex-col gap-4">
        <SalesPrice
          listPrice={product.listPrice}
          salesPrice={product.salesPrice}
        />
        <BuildProtectionCta
          variant="text"
          label={viewDetailsLabel || "View details"}
          link={{ linkType: "external", url: product.slug }}
          testId="build-protection-view-details"
          ariaLabel={`View details for ${product.name}`}
          className="self-start"
          onClick={handleViewDetails}
        />
      </div>
    </article>
  );
}

type BuildProtectionProductCardProps = {
  item: BuildProtectionProductItem;
  index: number;
  viewDetailsLabel?: string | null;
};
